import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/16/solid';
import classnames from 'classnames';
import find from 'lodash/find';
import { FC, Fragment, ReactNode } from 'react';
import { FieldRenderProps } from 'react-final-form';
import { twMerge } from 'tailwind-merge';

export type FormDropDownOptionOrValue =
  | { id: string }
  | { value: string | number; label?: string }
  | string
  | number;

type FormDropdownProps = FieldRenderProps<
  FormDropDownOptionOrValue,
  HTMLElement
> & {
  labelText?: string;
  placeHolder?: string;
  options: FormDropDownOptionOrValue[];
  renderOption: (option: FormDropDownOptionOrValue) => ReactNode;
  disabled?: boolean;
  onChange?: (value: FormDropDownOptionOrValue) => void;
  includeNoneOption?: boolean;
  labelClassName?: string;
};

const FormDropdownOption: FC<{
  option: FormDropDownOptionOrValue;
  renderOption: (option: FormDropDownOptionOrValue) => ReactNode;
}> = ({ option, renderOption }) => {
  return (
    <Listbox.Option
      className={({ active, selected }) =>
        classnames(
          'relative cursor-default select-none py-2 pl-3 pr-9',
          { 'bg-gray-100 text-black': active && !selected },
          { 'bg-secondaryBlue text-white': selected },
        )
      }
      value={option}
    >
      {({ selected }) => (
        <>
          <div className="flex items-center">
            {option === '' ? <span>&#8203;</span> : renderOption(option)}
          </div>
          {selected ? (
            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-white">
              <CheckIcon className="size-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      )}
    </Listbox.Option>
  );
};

const FormDropdown: FC<FormDropdownProps> = ({
  options,
  input,
  renderOption,
  labelText,
  placeHolder = 'Select Option...',
  meta: { touched, error },
  disabled,
  onChange,
  includeNoneOption = false,
  labelClassName,
}) => {
  const value = find(options, (option) => {
    if (typeof option === 'object' && typeof input.value === 'object') {
      // Check for id property
      if ('id' in option && 'id' in input.value) {
        return option.id === input.value.id;
      }
      // Check for value property (for radius filter)
      if ('value' in option && 'value' in input.value) {
        return option.value === input.value.value;
      }
      return false;
    }

    if (typeof option === 'string' && typeof input.value === 'string') {
      return option === input.value;
    }

    if (typeof option === 'number' && typeof input.value === 'number') {
      return option === input.value;
    }

    return false;
  });
  return (
    <>
      <Listbox
        value={value ?? ''}
        onChange={(value) => {
          input.onChange(value);
          if (onChange) {
            onChange(value);
          }
        }}
        disabled={disabled}
      >
        {({ open }) => (
          <>
            <Label
              className={classnames(
                twMerge('text-sm text-primaryGray mb-6', labelClassName),
                {
                  'sr-only': !labelText,
                },
              )}
            >
              {labelText || input.name}
            </Label>
            <div className="relative">
              <ListboxButton
                className={classnames(
                  'relative w-full cursor-default rounded-md border border-gray-300  py-2 pl-3 pr-10 text-left text-sm',
                  {
                    'border-red-800': touched && error,
                    'bg-white': !disabled,
                    'bg-gray-100': disabled,
                  },
                )}
              >
                <span className="flex items-center">
                  {input.value ? (
                    renderOption(input.value)
                  ) : (
                    <span className="text-gray-500">{placeHolder}</span>
                  )}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="size-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </ListboxButton>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none">
                  {includeNoneOption && (
                    <FormDropdownOption
                      key="none"
                      option=""
                      renderOption={renderOption}
                    />
                  )}
                  {options?.map((option: FormDropDownOptionOrValue, index) => {
                    let key: string | number;
                    if (typeof option === 'object') {
                      if ('id' in option) {
                        key = option.id;
                      } else if ('value' in option) {
                        key = `value-${option.value}`;
                      } else {
                        key = `option-${index}`;
                      }
                    } else {
                      key = option;
                    }

                    return (
                      <FormDropdownOption
                        key={key}
                        option={option}
                        renderOption={renderOption}
                      />
                    );
                  })}
                </ListboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      {touched && error && (
        <span className="text-xs text-red-800">{error}</span>
      )}
    </>
  );
};

export default FormDropdown;
